import { APP_INITIALIZER, enableProdMode, ErrorHandler, importProvidersFrom } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';


import { environment } from 'environments/environment';
import { hmrBootstrap } from 'hmr';
import { AppComponent } from './app/app.component';
import { SampleModule } from 'app/main/sample/sample.module';
import { LayoutModule } from 'app/layout/layout.module';
import { fuseConfig } from 'app/fuse-config';
import { FuseModule } from '@fuse/fuse.module';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { TranslateModule } from '@ngx-translate/core';

import { RedirectedComponent } from './app/main/smart-locks/redirected/redirected.component';
import { AuthGuard } from './app/guards/auth.guard';
import { LoggedInRedirectGuard } from './app/guards/logged-in-redirect.guard';
import { withHashLocation, provideRouter, Routes, Router } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { SafeHtmlReports } from './app/main/reports/safeHtml.pipe';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { SessionInterceptor } from './app/guards/interceptors/session.interceptor';
import { HTTP_INTERCEPTORS, withInterceptorsFromDi, provideHttpClient } from '@angular/common/http';
import * as Sentry from "@sentry/angular";

Sentry.init({
  dsn: "https://71d99db70d3fba6471bb9b77e9ff1bbd@o4509079718133761.ingest.us.sentry.io/4510144613384192",
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  environment:environment.mode,
  tracesSampleRate: 1.0,
  tracePropagationTargets: [],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  enableLogs: true,
  enabled: fuseConfig.enabledSentry
});

const appRoutes: Routes = [
    {
        path: "",
        children: [
            {
                path: "",
                redirectTo: "profile",
                pathMatch: "full",
            },
            {
                path: "*",
                redirectTo: "invitations",
            },
            {
                path: "dashboard",
                loadComponent: () => import("./app/main/dashboard/dashboard.component").then(m => m.DashboardComponent),
            },
            {
                path: "invitations",
                loadChildren: () => import("./app/main/owner/invitations/invitations.module").then(m => m.InvitationsModule),
            },
            {
                path: "profile",
                loadChildren: () => import("./app/main/owner/profile/profile.module").then(m => m.ProfileModule),
            },
            {
                path: "security",
                loadChildren: () => import("./app/main/security/security.module").then(m => m.SecurityModule),
            },
            {
                path: "building",
                loadChildren: () => import("./app/main/building/building.module").then(m => m.BuildingModule),
            },
            {
                path: "buildings",
                loadChildren: () => import("./app/main/buildings/buildings.module").then(m => m.BuildingsModule),
            },
            {
                path: "users",
                loadChildren: () => import("./app/main/users/users.module").then(m => m.UsersModule),
            },
            {
                path: "owner",
                loadChildren: () => import("./app/main/owner/owner.module").then(m => m.OwnerModule),
            },
            {
                path: "connectors",
                loadChildren: () => import("./app/main/gtconnector/connectors.module").then(m => m.ConnectorsModule),
            },
            {
                path: "guest-type",
                loadChildren: () => import("./app/main/guest-type/guest-type.module").then(m => m.GuestTypeModule),
            },
            {
                path: "tips",
                loadChildren: () => import("./app/main/tips/tips.module").then(m => m.TipsModule),
            },
            {
                path: "dealers",
                loadChildren: () => import("./app/main/dealers/dealers.module").then(m => m.DealersModule),
            },
            {
                path: "licenses",
                loadChildren: () => import("./app/main/licenses/licenses.module").then(m => m.LicensesModule),
            },
            {
                path: "kiosks",
                loadChildren: () => import("./app/main/kiosks/kiosks.module").then(m => m.KiosksModule),
            },
            {
                path: "qr-readers",
                loadChildren: () => import("./app/main/qr-readers/qr-readers.module").then(m => m.QrReadersModule),
            },
            {
                path: "security-licenses",
                loadChildren: () => import("./app/main/security-licenses/security-licenses.module").then(m => m.SecurityLicensesModule),
            },
            {
                path: "resident-moves",
                loadChildren: () => import("./app/main/resident-moves/resident-moves.module").then(m => m.ResidentMovesModule),
            },
            {
                path: "speed-violation",
                loadChildren: () => import("./app/main/speed-violation/speed-violation.module").then(m => m.SpeedViolationModule),
            },
        ],
        canActivate: [AuthGuard],
    },
    {
        path: "auth",
        loadChildren: () => import("./app/main/auth/auth.module").then(m => m.AuthModule),
        canActivate: [LoggedInRedirectGuard],
    },
    {
        path: 'invitation-request',
        loadChildren: () => import("./app/main/invitation-request/invitation-request.module").then(m => m.InvitationRequestModule)
    },
    {
        path: 'passview',
        loadChildren: () => import("./app/main/passview/passview.module").then(m => m.PassviewModule)
    },
    {
        path: 'residentform',
        loadChildren: () => import("./app/main/residentform/residentform.module").then(m => m.ResidentformModule)
    },
    {
        path: "smartlocks/settings/ks/redirect",
        component: RedirectedComponent,
    },
];


if ( environment.production )
{
    enableProdMode();
}

const bootstrap = () => bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(BrowserModule, TranslateModule.forRoot(), 
        // Material moment date module
        MatMomentDateModule, 
        // Material
        MatButtonModule, MatIconModule, MatInputModule, MatProgressSpinnerModule, 
        // Fuse modules
        FuseModule.forRoot(fuseConfig), 
        // App modules
        LayoutModule, SampleModule
    ),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SessionInterceptor,
            multi: true,
        },
        {
            provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
            useValue: { panelClass: ["custom-snackbar-theme"] },
        },
        SafeHtmlReports,
        provideAnimations(),
        provideHttpClient(withInterceptorsFromDi()),
        provideRouter(appRoutes, withHashLocation()),
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
        },
    ]
});

if ( environment.hmr )
{
    if ( module['hot'] )
    {
        // hmrBootstrap(module, bootstrap);
    }
    else
    {
        console.error('HMR is not enabled for webpack-dev-server!');
        console.log('Are you using the --hmr flag for ng serve?');
    }
}
else
{
    bootstrap().catch(err => console.log(err));
}
